import React, { useState, useEffect } from "react";
import {
  Box, Typography, TextField, Button, Grid,
  FormControl, InputLabel, Select, MenuItem, Chip,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useIntl } from "react-intl";

import { createJob } from "../../api/companyApi";
import { fetchRoles } from "../../api/marketApi";
import { useNotification } from "../../providers/NotificationProvider";
import { COMPANY_ID } from "../../config/appConfig";

const getLang = () => (window.navigator.language.startsWith("ja") ? "ja" : "en");

export default function JobCreatePage() {
  const { businessDomainId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const [form, setForm] = useState({ jobName: "", jobDescription: "", url: "" });
  const [marketRoles, setMarketRoles] = useState([]);
  const [selectedRoleIds, setSelectedRoleIds] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const roles = await fetchRoles();
        setMarketRoles(Array.isArray(roles) ? roles : []);
      } catch (e) {
        console.error("Failed to load roles:", e);
      }
    })();
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedRoleIds.length === 0) {
      notify.error(t("job.role.required"));
      return;
    }
    try {
      const roles = selectedRoleIds.map((id) => {
        // Use String() comparison to handle potential number/string type mismatch
        const role = marketRoles.find((r) => String(r.id) === id);
        return {
          roleId: role ? role.id : id,
          roleName: role ? (getLang() === "ja" ? role.japaneseName : role.englishName) : id,
        };
      });
      await createJob({
        ...form,
        url: form.url || null,
        companyId: COMPANY_ID,
        businessDomainId,
        roles,
      });
      notify.success(t("message.create.success"));
      navigate(`/design/${businessDomainId}/jobs`);
    } catch (err) {
      notify.apiError(err);
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {t("job.create.title")}
      </Typography>

      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <TextField
              fullWidth
              required
              multiline
              label={t("job.name")}
              name="jobName"
              value={form.jobName}
              onChange={handleChange}
            />
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label={t("job.description")}
              name="jobDescription"
              value={form.jobDescription}
              onChange={handleChange}
            />
          </Grid>

          <Grid size={12}>
            <FormControl fullWidth required>
              <InputLabel>{t("job.role")}</InputLabel>
              <Select
                multiple
                value={selectedRoleIds}
                onChange={(e) => setSelectedRoleIds(e.target.value)}
                label={t("job.role")}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((id) => {
                      const role = marketRoles.find((r) => String(r.id) === id);
                      return (
                        <Chip
                          key={id}
                          label={role ? (getLang() === "ja" ? role.japaneseName : role.englishName) : id}
                          size="small"
                        />
                      );
                    })}
                  </Box>
                )}
              >
                {marketRoles.map((role) => (
                  <MenuItem key={role.id} value={String(role.id)}>
                    {getLang() === "ja" ? role.japaneseName : role.englishName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth
              type="url"
              label={t("common.url")}
              name="url"
              value={form.url}
              onChange={handleChange}
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button variant="contained" type="submit">
            {t("common.add")}
          </Button>
          <Button variant="outlined" onClick={() => navigate(`/design/${businessDomainId}/jobs`)}>
            {t("common.cancel")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
